import { useMemo } from 'react'

import {
  GetBenchmarkRoleMappingsDocument,
  GetMercerSalaryDataDocument,
  GetMercerSurveyRegistriesDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { BenchmarkMatchResult } from 'modules/job_catalog/benchmark/types'
import { toDominanceFlag, toNumber } from 'modules/job_catalog/benchmark/lib/parsers'

/**
 * Fetches benchmark (Mercer) salary data for a tree node across ALL provided
 * country codes at once, returning a Map of countryCode → BenchmarkMatchResult.
 *
 * Used by SalariesCard to show per-workplace market position indicators
 * without needing one hook call per country.
 */
export const useBenchmarkDataByCountry = (
  treeNodeUuid: string,
  countryCodes: string[],
  enabled = true
) => {
  // ── Deduplicate and normalize country codes ──
  const upperCountryCodes = useMemo(
    () => [...new Set(countryCodes.filter(Boolean).map((c) => c.toUpperCase()))].sort(),
    [countryCodes]
  )

  const shouldFetch = enabled && upperCountryCodes.length > 0

  // ── Query 1: Resolve treeNodeUuid → externalJobCode ──
  const { data: mappingsData, loading: mappingsLoading } = useQuery(
    GetBenchmarkRoleMappingsDocument,
    {
      variables: { treeNodeUuids: [treeNodeUuid], activeOnly: true },
      skip: !shouldFetch || !treeNodeUuid,
    }
  )

  const activeMapping = useMemo(
    () => mappingsData?.jobCatalog?.benchmarkRoleMappingsConnection?.nodes?.[0] ?? null,
    [mappingsData]
  )

  // ── Query 2: Get ALL surveys for ALL country codes at once ──
  const { data: registryData, loading: registryLoading } = useQuery(
    GetMercerSurveyRegistriesDocument,
    {
      variables: { countryCodes: upperCountryCodes },
      skip: !shouldFetch || !activeMapping,
    }
  )

  // Build a map: countryCode → latest survey
  const surveyByCountry = useMemo(() => {
    const map = new Map<
      string,
      {
        surveyCode: string
        currency: string | null
        year: number | null
        countryCode: string | null
      }
    >()
    const nodes = registryData?.jobCatalog?.mercerSurveyRegistriesConnection?.nodes ?? []
    for (const node of nodes) {
      if (!node?.countryCode || !node?.surveyCode) continue
      const normalizedCountryCode = node.countryCode.toUpperCase()
      const existing = map.get(normalizedCountryCode)
      if (!existing || (node.year ?? 0) > (existing.year ?? 0)) {
        map.set(normalizedCountryCode, {
          surveyCode: node.surveyCode,
          currency: node.currency ?? null,
          year: node.year ?? null,
          countryCode: node.countryCode ?? null,
        })
      }
    }
    return map
  }, [registryData])

  // Collect all resolved survey codes for the salary data query
  const allSurveyCodes = useMemo(
    () => [...new Set([...surveyByCountry.values()].map((s) => s.surveyCode))],
    [surveyByCountry]
  )

  // ── Query 3: Get salary data for ALL surveys at once ──
  const { data: salaryData, loading: salaryLoading } = useQuery(GetMercerSalaryDataDocument, {
    variables: {
      mercerJobCodes: activeMapping ? [activeMapping.externalJobCode] : [],
      surveyCodes: allSurveyCodes,
    },
    skip: !shouldFetch || !activeMapping || allSurveyCodes.length === 0,
  })

  // Build a map: surveyCode → salary node
  const salaryBySurveyCode = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof salaryData>['jobCatalog']['mercerSalaryDataConnection']['nodes'][number]
    >()
    const nodes = salaryData?.jobCatalog?.mercerSalaryDataConnection?.nodes ?? []
    for (const node of nodes) {
      if (!node?.surveyCode) continue
      const existing = map.get(node.surveyCode)
      if (!existing) {
        map.set(node.surveyCode, node)
      } else {
        // Keep the one with the latest updatedAt as safety net
        const existingTs = existing?.updatedAt ? new Date(existing.updatedAt).getTime() : 0
        const nodeTs = node?.updatedAt ? new Date(node.updatedAt).getTime() : 0
        if (nodeTs > existingTs) {
          map.set(node.surveyCode, node)
        }
      }
    }
    return map
  }, [salaryData])

  // ── Build final map: countryCode (lowercase) → BenchmarkMatchResult ──
  const dataByCountry = useMemo(() => {
    const map = new Map<string, BenchmarkMatchResult>()
    if (!enabled) return map

    if (!activeMapping) {
      for (const upperCode of upperCountryCodes) {
        map.set(upperCode.toLowerCase(), { status: 'unmapped', companySalaryRange: null })
      }

      return map
    }

    for (const upperCode of upperCountryCodes) {
      const lowerCode = upperCode.toLowerCase()
      const survey = surveyByCountry.get(upperCode) ?? null

      if (!survey) {
        map.set(lowerCode, { status: 'no_data', companySalaryRange: null })
        continue
      }

      const salaryNode = salaryBySurveyCode.get(survey.surveyCode) ?? null

      if (!salaryNode) {
        map.set(lowerCode, { status: 'no_data', companySalaryRange: null })
        continue
      }

      const obsCount = salaryNode.totalGuaranteedAnnualNumObs
      if (obsCount == null || obsCount < 5) {
        map.set(lowerCode, {
          status: 'suppressed',
          companySalaryRange: null,
          obsCount: obsCount ?? null,
        })
        continue
      }

      map.set(lowerCode, {
        status: 'matched',
        companySalaryRange: null,
        tgaP25: toNumber(salaryNode.totalGuaranteedAnnualPercent25),
        tgaP50: toNumber(salaryNode.totalGuaranteedAnnualMedian),
        tgaP75: toNumber(salaryNode.totalGuaranteedAnnualPercent75),
        tgaMean: toNumber(salaryNode.totalGuaranteedAnnualMean),
        orgCount: salaryNode.totalGuaranteedAnnualOrgs ?? null,
        obsCount: salaryNode.totalGuaranteedAnnualNumObs ?? null,
        distinctOrgCount: salaryNode.totalGuaranteedAnnualDistOrgs ?? null,
        dominanceFlag: toDominanceFlag(salaryNode.totalGuaranteedAnnualDominance),
        currency: survey.currency,
        year: survey.year,
        countryCode: survey.countryCode,
      })
    }

    return map
  }, [activeMapping, enabled, salaryBySurveyCode, surveyByCountry, upperCountryCodes])

  return {
    dataByCountry,
    loading: enabled && (mappingsLoading || registryLoading || salaryLoading),
  }
}
